import type { Conversation, Period, RunState } from '../v4/contracts'

/** A method file can go to the run until its plan is approved, while the run waits on the engineer. */
export function canSendFile(run: RunState): boolean {
  return (
    !run.refusal &&
    !run.events.some((e) => e.type === 'plan_approved') &&
    ['parked', 'awaiting_approval', 'awaiting_ask'].includes(run.status)
  )
}

/**
 * The plan awaiting approval is a new version of a run that already ran: an earlier version was
 * approved. Approving it carries the run on (the steps already done are kept); it is not a fresh
 * start, so the page must not ask as if it were. `edited` when the engineer edited this version (the
 * engine marks the version's `plan_proposed` as edited), `found` when the engine wrote it because a
 * step found something; null for a plan nobody approved yet.
 */
export function continuesRun(run: RunState): 'found' | 'edited' | null {
  if (!run.events.some((e) => e.type === 'plan_approved' && e.planVersion < run.planVersion)) return null
  const proposed = [...run.events].reverse().find((e) => e.type === 'plan_proposed' && e.planVersion === run.planVersion)
  return (proposed?.payload as { edited?: unknown } | undefined)?.edited === true ? 'edited' : 'found'
}

/** The period the approved plan read, to start a rerun from: the first gather step's period. */
export function planPeriod(run: RunState): Period | null {
  const approved = [...run.events].reverse().find((e) => e.type === 'plan_approved')?.planVersion
  const plan = run.trace.planVersions.find((p) => p.version === approved) ?? run.trace.planVersions.at(-1)
  for (const step of plan?.steps ?? []) {
    const period = (step.op as { period?: Period } | undefined)?.period
    if (period?.from && period?.to) return period
  }
  return null
}

/** The approved plan can run again on another period: the run ended, and a plan of it was approved. */
export const canRerun = (run: RunState): boolean =>
  run.status === 'finished' && run.events.some((e) => e.type === 'plan_approved')

/**
 * The conversation a question typed over this run carries, so the engine reads it as a follow-up:
 * the run's question and its answer's headline (the first turn the run wrote, which the engine
 * matches exactly), then the new question. Null while the run has no answer: a question then is
 * a new run of its own.
 */
export function followUpConversation(run: RunState, question: string): Conversation | null {
  const headline = run.trace.answer?.headline.trim()
  if (!headline) return null
  return {
    turns: [
      { role: 'user', text: run.question },
      { role: 'assistant', text: headline },
      { role: 'user', text: question },
    ],
    resolved: {},
  }
}
